export interface AppFeature {
  title: string;
  description: string;
  icon: string;
}

export interface AppDetails {
  id: string;
  name: string;
  shortDescription: string;
  fullDescription: string;
  icon: string;
  color: string;
  launchUrl?: string; // Optionnel car récupéré depuis config
  launchType: 'expo' | 'web' | 'native';
  features: AppFeature[];
  technologies: string[];
  useCases: string[];
}

export const appsData: AppDetails[] = [
  {
    id: 'mobile',
    name: 'Application mobile',
    shortDescription: 'L\'application pour les utilisateurs finaux. Trouvez des parkings, créez des tickets et gérez votre portefeuille.',
    fullDescription: 'L\'application mobile Tunisie Parking révolutionne votre expérience de stationnement en Tunisie. Conçue pour les utilisateurs finaux, elle offre une interface intuitive et moderne pour gérer tous vos besoins de parking en quelques touches.',
    icon: '📱',
    color: 'from-blue-500 to-blue-600',
    launchUrl: '',
    launchType: 'expo',
    features: [
      {
        title: 'Carte interactive en temps réel',
        description: 'Visualisez tous les parkings disponibles sur une carte interactive mise à jour en temps réel. Géolocalisation précise, filtres par disponibilité, prix et distance.',
        icon: '🗺️',
      },
      {
        title: 'Gestion complète des tickets',
        description: 'Créez des tickets de stationnement en quelques secondes, prolongez-les à tout moment, annulez-les si nécessaire. Timer intégré avec alertes de fin de période.',
        icon: '🎫',
      },
      {
        title: 'Portefeuille électronique',
        description: 'Rechargez votre wallet directement depuis l\'application. Suivez vos transactions, consultez votre historique et gérez votre solde en toute simplicité.',
        icon: '💳',
      },
      {
        title: 'Gestion des véhicules',
        description: 'Ajoutez et gérez plusieurs véhicules avec leurs plaques d\'immatriculation tunisiennes. Sélection rapide lors de la création de tickets.',
        icon: '🚗',
      },
      {
        title: 'Notifications push',
        description: 'Recevez des alertes en temps réel : temps restant, possibilité de prolongation, rappels de fin de ticket, et notifications importantes.',
        icon: '🔔',
      },
      {
        title: 'Authentification sécurisée',
        description: 'Connexion rapide et sécurisée via OTP SMS. Votre compte est protégé par une authentification JWT robuste.',
        icon: '🔐',
      },
      {
        title: 'Profil utilisateur',
        description: 'Consultez vos statistiques de stationnement, historique des tickets, et personnalisez vos préférences dans votre espace personnel.',
        icon: '👤',
      },
      {
        title: 'Paiements sécurisés',
        description: 'Intégration avec Flouci/D17 pour des paiements sécurisés. Transactions rapides et traçables avec historique complet.',
        icon: '💰',
      },
    ],
    technologies: ['React Native', 'Expo', 'Nativewind', 'React Query', 'TypeScript'],
    useCases: [
      'Rechercher un parking disponible près de votre destination',
      'Créer un ticket de stationnement rapidement',
      'Prolonger un ticket en cours d\'utilisation',
      'Recharger votre wallet avant de partir',
      'Suivre l\'historique de vos tickets et dépenses',
      'Gérer plusieurs véhicules depuis une seule application',
    ],
  },
  {
    id: 'controller',
    name: 'Application contrôleur',
    shortDescription: 'Application dédiée aux contrôleurs municipaux pour vérifier les tickets et scanner les plaques d\'immatriculation.',
    fullDescription: 'L\'application Contrôleur Tunisie Parking est spécialement conçue pour les agents municipaux et contrôleurs de stationnement. Optimisée pour une utilisation sur le terrain, elle permet de vérifier rapidement la validité des tickets et d\'effectuer des contrôles efficaces.',
    icon: '🚗',
    color: 'from-green-500 to-green-600',
    launchUrl: '',
    launchType: 'expo',
    features: [
      {
        title: 'Scan de plaques automatique',
        description: 'Scannez les plaques d\'immatriculation tunisiennes avec la caméra de votre appareil. Reconnaissance OCR avancée avec MLKit pour une saisie instantanée.',
        icon: '📷',
      },
      {
        title: 'Vérification tickets en temps réel',
        description: 'Vérifiez instantanément si un véhicule possède un ticket valide. Informations détaillées : durée restante, parking utilisé, véhicule associé.',
        icon: '✅',
      },
      {
        title: 'Historique des contrôles',
        description: 'Conservez un historique complet de tous vos contrôles effectués. Recherchez par date, plaque, ou statut du ticket pour un suivi optimal.',
        icon: '📋',
      },
      {
        title: 'Interface terrain optimisée',
        description: 'Interface sombre adaptée à l\'utilisation en extérieur, navigation simple à une main, et mode hors-ligne pour les zones à faible couverture.',
        icon: '🌙',
      },
      {
        title: 'Notifications de violations',
        description: 'Générez automatiquement des notifications pour les véhicules sans ticket valide. Rapport détaillé avec horodatage et localisation.',
        icon: '⚠️',
      },
      {
        title: 'Authentification sécurisée',
        description: 'Accès réservé aux contrôleurs autorisés avec authentification par code PIN ou biométrique pour sécuriser l\'accès à l\'application.',
        icon: '🔒',
      },
      {
        title: 'Statistiques quotidiennes',
        description: 'Consultez vos statistiques de contrôles : nombre de véhicules vérifiés, violations détectées, et performance de la journée.',
        icon: '📊',
      },
      {
        title: 'Synchronisation automatique',
        description: 'Synchronisation automatique des données avec le serveur central. Vos contrôles sont enregistrés en temps réel dans le système.',
        icon: '☁️',
      },
    ],
    technologies: ['React Native', 'Expo', 'Vision Camera', 'MLKit', 'TypeScript'],
    useCases: [
      'Scanner une plaque d\'immatriculation rapidement',
      'Vérifier la validité d\'un ticket de stationnement',
      'Consulter l\'historique des contrôles de la journée',
      'Générer un rapport de violation',
      'Effectuer des contrôles en mode hors-ligne',
      'Synchroniser les données avec le serveur central',
    ],
  },
  {
    id: 'web-admin',
    name: 'Web admin',
    shortDescription: 'Tableau de bord web pour la gestion complète du système : parkings, utilisateurs, tickets et analytics.',
    fullDescription: 'Le tableau de bord Web Admin Tunisie Parking est la solution complète de gestion et d\'administration du système de parking. Destiné aux administrateurs, il offre une vue d\'ensemble complète avec des outils puissants pour gérer tous les aspects de la plateforme.',
    icon: '💼',
    color: 'from-purple-500 to-purple-600',
    launchUrl: '',
    launchType: 'web',
    features: [
      {
        title: 'Dashboard analytics complet',
        description: 'Tableau de bord interactif avec KPIs en temps réel : revenus, occupation des parkings, nombre de tickets actifs, statistiques d\'utilisation.',
        icon: '📊',
      },
      {
        title: 'Gestion des parkings',
        description: 'CRUD complet pour les parkings : création, modification, suppression. Géolocalisation précise, configuration des zones et tarifs associés.',
        icon: '🅿️',
      },
      {
        title: 'Gestion des utilisateurs',
        description: 'Administration complète des utilisateurs : création, modification des rôles (USER, CONTROLLER, ADMIN), consultation des profils et statistiques.',
        icon: '👥',
      },
      {
        title: 'Suivi des tickets',
        description: 'Visualisation en temps réel de tous les tickets actifs et historiques. Filtres avancés, recherche par utilisateur, véhicule ou parking.',
        icon: '🎫',
      },
      {
        title: 'Gestion des zones et tarifs',
        description: 'Configuration complète des zones de parking et de leurs tarifs. Calcul automatique des prix selon la durée et la zone.',
        icon: '📍',
      },
      {
        title: 'Rapports détaillés',
        description: 'Génération de rapports personnalisés : revenus par période, analyse d\'occupation, statistiques d\'utilisation, export CSV/PDF.',
        icon: '📄',
      },
      {
        title: 'Graphiques et visualisations',
        description: 'Graphiques interactifs avec Recharts : évolution des revenus, taux d\'occupation, tendances d\'utilisation, comparaisons temporelles.',
        icon: '📈',
      },
      {
        title: 'Gestion des transactions',
        description: 'Suivi complet de toutes les transactions financières : recharges de wallet, paiements de tickets, remboursements, historique détaillé.',
        icon: '💰',
      },
      {
        title: 'Configuration système',
        description: 'Paramètres généraux du système : configuration des notifications, intégrations de paiement, paramètres de sécurité et préférences.',
        icon: '⚙️',
      },
    ],
    technologies: ['React', 'Vite', 'Ant Design', 'Redux Toolkit', 'Recharts', 'TypeScript'],
    useCases: [
      'Consulter les statistiques et KPIs en temps réel',
      'Gérer les parkings et leurs configurations',
      'Administrer les utilisateurs et leurs rôles',
      'Suivre tous les tickets et transactions',
      'Générer des rapports et exports',
      'Configurer les zones et tarifs de stationnement',
      'Analyser les tendances avec des graphiques interactifs',
      'Surveiller la santé du système et les alertes',
    ],
  },
];

export const getAppById = (id: string): AppDetails | undefined => {
  return appsData.find(app => app.id === id);
};

